import {
	getLocalStorageValue,
	setLocalStorageValue,
} from "../../common/helpers/localStorageHelper";

const PREV_UID_KEY = "prevUid";

/**
 * Get previous UID from localStorage
 */
export const getPrevUid = (): string | null => {
	return getLocalStorageValue(PREV_UID_KEY, null);
};

/**
 * Set previous UID to localStorage
 */
export const setPrevUid = (uid: string | null): void => {
	if (uid) {
		setLocalStorageValue(PREV_UID_KEY, uid);
	} else {
		// If uid is null, remove the key
		localStorage.removeItem(PREV_UID_KEY);
	}
};

/**
 * Get current UID from cookie
 */
export const getCurrentUidFromCookie = (): string | null => {
	try {
		const cookies = document.cookie.split(";");
		const uidCookie = cookies.find((cookie) => cookie.trim().startsWith("uid="));

		return uidCookie ? decodeURIComponent(uidCookie.split("=")[1]) : null;
	} catch (error) {
		console.error("Failed to get UID from cookie", error);

		return null;
	}
};

/**
 * Compare UIDs and determine if cart should be cleared
 * @param current - Current UID from cookie
 * @param prev - Previous UID from localStorage
 * @returns true if cart should be cleared
 *
 * Cart clearing logic:
 * 1. Guest -> User (user login): Keep cart (inherit guest cart)
 * 2. User A -> User B (user switch): Clear cart
 * 3. User -> Guest (user logout): Clear cart
 * 4. Guest -> Guest: Keep cart
 */
export const shouldClearCart = (current: string | null, prev: string | null): boolean => {
	// Case 1: Guest -> User (user login)
	// If prev is null, "", or undefined - user is logging in from guest state
	// Action: Keep cart (inherit guest cart)
	if (!prev || prev === "") {
		return false;
	}

	// Case 2: User A -> User B (user switch)
	// Case 3: User -> Guest (user logout)
	// If current and prev are different - user switched or logged out
	// Action: Clear cart
	if (current !== prev) {
		return true;
	}

	// Case 4: User -> User (same user) or Guest -> Guest
	// No change in user state
	// Action: Keep cart
	return false;
};
